import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { Trans } from 'react-i18next'

import { store } from 'store/reducers'
import { getPositionName, getUserName, extractTranslation, parsePhone } from 'services/Utilities'
import { Format } from 'services/Dates'
import { Address, HideOverflow } from 'components/UI'
import hasAccessToThis from 'services/utilFunctions/hasAccessToThis'

import { t } from 'translations'

export default event => {
  const {
    employeeId,
    replacesThisEmployee,
    sce = [],
    isAbsent,
    isLate,
    arrivalTime,
    isLeavingEarly,
    departureTime,
    isExtraShift,
    isOvertime,
    isOnCall,
    isOnTimeoff,
    hasUnapprovedTimeoff,
    isIrreplaceable,
    needsReplacement,
    noEmployeeAvailable,
    hasSpecificRequirements,
    coveredOtherUnit,
    comment,
    tags = [],
    arrival,
    leave,
    start,
    end,
    positionId
  } = event
  const {
    resources: { usersById, tagsById },
    settings: { defaultTimeZone: timeZone }
  } = store.getState()

  // All parts
  let employeeInfo,
    contactInfo,
    replacementInfo,
    absentText,
    lateText,
    leaveEarlyText,
    primaryOccupation,
    secondaryOccupation,
    lastConfirmationInfo,
    shiftInfo = []

  const employeeAssigned = employeeId && usersById[employeeId] ? usersById[employeeId] : undefined

  if (employeeAssigned) {
    const { _id, priorityNumber, occupations: { primary, secondary } = {}, phoneNumbers = [] } = usersById[employeeId]
    employeeInfo = (
      <>
        {/* Priority number is optional and depends on a setting */}
        {priorityNumber ? `(${priorityNumber})` : ''} {getUserName(_id, { showEmployeeNumber: true })}
      </>
    )

    primaryOccupation = getPositionName(primary)
    secondaryOccupation = getPositionName(secondary)
    if (hasAccessToThis('userSeeContactInfo') && phoneNumbers.length > 0)
      contactInfo = (
        <div>
          <i className="fas fa-phone mr-1"></i>
          {phoneNumbers.map(({ phoneNumber }) => parsePhone(phoneNumber)).join(', ')}
        </div>
      )
  } else employeeInfo = t('manager.schedule.UNASSIGNED_SHIFT')

  if (replacesThisEmployee) replacementInfo = t('manager.schedule.REPLACES_THIS_EMPLOYEE', { name: getUserName(replacesThisEmployee) })

  if (isAbsent && hasAccessToThis('manageAttendance')) absentText = t('manager.schedule.IS_ABSENT')
  if (isLate && hasAccessToThis('manageAttendance')) lateText = t('manager.schedule.IS_ARRIVING_AT', { arrivalTime })
  if (isLeavingEarly && hasAccessToThis('manageAttendance')) leaveEarlyText = t('manager.schedule.IS_LEAVING_AT', { departureTime })
  if (isOnTimeoff && hasAccessToThis('manageTimeOff')) shiftInfo.push(t('manager.schedule.IN_TIME_OFF'))
  if (hasUnapprovedTimeoff && hasAccessToThis('manageTimeOff')) shiftInfo.push(t('manager.schedule.UNAPPROVED_TIME_OFF'))
  if (isExtraShift) shiftInfo.push(t('manager.schedule.VARIABLE_END_TIME'))
  if (isOvertime) shiftInfo.push(t('manager.schedule.OVERTIME'))
  if (isOnCall) shiftInfo.push(t('manager.schedule.ON_CALL'))
  if (isIrreplaceable) shiftInfo.push(t('manager.schedule.IS_IRREPLACEABLE'))
  if (needsReplacement) shiftInfo.push(t('manager.schedule.NEEDS_REPLACEMENT'))
  if (noEmployeeAvailable) shiftInfo.push(t('manager.schedule.NO_EMPLOYEE_AVAILABLE'))
  if (hasSpecificRequirements) shiftInfo.push(t('manager.schedule.HAS_SPECIFIC_REQUIREMENTS'))
  if (coveredOtherUnit) shiftInfo.push(t('manager.schedule.COVEREDOTHERUNIT'))

  // If there is an issue the user can't see
  const hasAnAttendanceRequestAndCantSeeIt = (isAbsent || isLate || isLeavingEarly) && !hasAccessToThis('manageAttendance')
  const hasATimeOffAndCantSeeIt = isOnTimeoff && !hasAccessToThis('manageTimeOff')
  const hasATimeOffUnapprovedAndCantSeeIt = hasUnapprovedTimeoff && !hasAccessToThis('manageTimeOff')
  const hasSomeRequirements = hasSpecificRequirements && !hasAccessToThis('userManageSpecificRestrictions')
  if (hasAnAttendanceRequestAndCantSeeIt || hasATimeOffAndCantSeeIt || hasSomeRequirements || hasATimeOffUnapprovedAndCantSeeIt)
    shiftInfo.push(t('manager.schedule.UNAVAILABLE_ISSUES'))

  // Confirmation status
  const lastConfirmation = [...sce].shift()

  if (typeof lastConfirmation === 'object' && lastConfirmation.type !== 'cancellation' && hasAccessToThis('scheduleConfirm')) {
    let lastConfirmationKey
    switch (lastConfirmation.method) {
      case 'email':
        if (lastConfirmation.events.confirmed) lastConfirmationKey = 'confirmed'
        else if (lastConfirmation.events.opened) lastConfirmationKey = 'opened'
        else if (lastConfirmation.events.delivered) lastConfirmationKey = 'delivered'
        else if (lastConfirmation.events.failed) lastConfirmationKey = 'failed'
        else if (lastConfirmation.events.accepted) lastConfirmationKey = 'accepted'
        break
      case 'phone':
        if (lastConfirmation.events.noPhone) lastConfirmationKey = 'noPhone'
        else if (lastConfirmation.events.failed) lastConfirmationKey = 'failed'
        else if (lastConfirmation.events.confirmed) lastConfirmationKey = 'confirmed'
        break
      default:
        break
    }

    if (lastConfirmationKey)
      lastConfirmationInfo = {
        method: lastConfirmation.method,
        date: lastConfirmation.events[lastConfirmationKey],
        eventKey: lastConfirmationKey,
        confirmedBy: lastConfirmation.confirmedBy
      }
  }

  // Tags
  const locations = tags.filter(tag => tagsById[tag]?.type === 'locations').filter(Boolean)
  const otherTags = tags.filter(tag => !['locations'].includes(tagsById[tag]?.type)).filter(Boolean)
  // TODO: Add punch text

  // Daypilot does not support jsx except in specific cases and it is cleaner and less error prone to use jsx than build an html string manually.
  return ReactDOMServer.renderToString(
    <>
      <div>
        <b>{employeeInfo}</b>
        {/* Occupations */}
        {primaryOccupation || secondaryOccupation ? <i className="ml-1">({[primaryOccupation, secondaryOccupation].filter(Boolean).join(' / ')})</i> : null}
        {/* Indicates if employee was replaced */}
        <span className="ml-1">{replacementInfo}</span>
      </div>

      {contactInfo}

      <div>
        <b className="mr-1">
          {Format(start, { outFormat: 'time' })} - {Format(end, { outFormat: 'time' })}
        </b>
        {getPositionName(positionId)}
      </div>

      {/* Attendance */}
      {(absentText || lateText || leaveEarlyText) && hasAccessToThis('manageAttendance') ? (
        <div className="font-weight-bold">{[absentText, lateText, leaveEarlyText].filter(Boolean).join(' / ')}</div>
      ) : null}

      {/* Punch info */}
      {hasAccessToThis('punchCardSee') ? (
        <>
          {arrival ? <div>{t('manager.schedule.PUNCH_ARRIVAL', { time: Format(arrival, { outFormat: 'timeComplete' }) })}</div> : null}
          {leave ? <div>{t('manager.schedule.PUNCH_LEAVE', { time: Format(leave, { outFormat: 'timeComplete' }) })}</div> : null}
        </>
      ) : null}

      {/* Comment */}
      {comment && comment.length ? (
        <div className="font-italic">
          <i className={`fa fa-comment mr-1`} />
          <HideOverflow parsedValues={extractTranslation(comment, { extractCurrentLanguage: true }).split('')} hideOverflow={100} delimiter={''} hideButton />
        </div>
      ) : null}

      {/* Locations */}
      {locations.map(location => {
        const { icon, color, address = {}, name } = tagsById[location]

        return (
          <div key={location}>
            <i className={`fa ${icon} mr-1`} style={{ color: color ?? '' }} />
            <b>{name}</b>
            <div className="font-italic">
              <Address address={address} short />
            </div>
          </div>
        )
      })}

      {/* Other tags */}
      {otherTags.map(tag => {
        const { icon, name, color } = tagsById[tag]
        return (
          <div key={tag}>
            <i className={`fa ${icon} mr-1`} style={{ color: color ?? '' }} />
            {name}
          </div>
        )
      })}

      {/* Additional shift info */}
      {shiftInfo.join(' / ')}

      {/* Confirmation info */}
      {lastConfirmationInfo && hasAccessToThis('scheduleConfirm') ? (
        <div
          className={
            { confirmed: 'text-success', opened: 'text-success', delivered: 'text-success', failed: 'text-danger', noPhone: 'text-danger' }[
              lastConfirmationInfo.eventKey
            ]
          }
        >
          <i className={`fa ${{ email: 'fa-envelope', phone: 'fa-phone' }[lastConfirmationInfo.method]} mr-1`}></i>
          <Trans
            t={t}
            i18nKey={`manager.schedule.EDIT_MODAL.CONFIRMATION_EVENTS.${lastConfirmationInfo.eventKey.toUpperCase()}_${lastConfirmationInfo.method.toUpperCase()}`}
          >
            <b>{{ name: getUserName(lastConfirmationInfo.confirmedBy) }}</b>
            <b>{{ time: Format(lastConfirmationInfo.date, { outFormat: 'time', isUTC: true, timeZone }) }}</b>
            {{ date: Format(lastConfirmationInfo.date, { outFormat: 'humanDate', isUTC: true, timeZone }) }}
          </Trans>
        </div>
      ) : null}
    </>
  )
}
